import { GENERATOR_TYPES, SIZE_PRESETS, runGenerator } from '../GeneratorRegistry.js';
import { LocalContentProvider } from '../LocalContentProvider.js';
import { parseTMJ } from '../../core/TMJParser.js';
import { el } from '../../utils/dom.js';

/**
 * Generator panel UI — type selector, size preset, seed input, options, generate button.
 */
export class GeneratorPanel {
  constructor(state, mapCanvas, compositor) {
    this.state = state;
    this.mapCanvas = mapCanvas;
    this.compositor = compositor;
    this.contentProvider = new LocalContentProvider(state);
    this._dialog = null;
    this._worker = null;
    this._initWorker();
  }

  _initWorker() {
    try {
      this._worker = new Worker(
        new URL('../../workers/generatorWorker.js', import.meta.url),
        { type: 'module' },
      );
    } catch {
      // Worker not available (e.g. file:// protocol) — will use main thread fallback
      this._worker = null;
    }
  }

  show() {
    if (this._dialog) this._dialog.remove();

    const overlay = el('div', { className: 'dialog-overlay' });
    const dialog = el('div', { className: 'dialog generator-dialog' });

    // Title
    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Generate Map'));

    // Type selector
    const typeGroup = this._createFormGroup('Type');
    const typeSelect = el('select', { id: 'gen-type' });
    for (const [key, entry] of Object.entries(GENERATOR_TYPES)) {
      typeSelect.appendChild(el('option', { value: key }, `${entry.name} — ${entry.description}`));
    }
    typeGroup.appendChild(typeSelect);
    dialog.appendChild(typeGroup);

    // Size preset
    const sizeGroup = this._createFormGroup('Size');
    const sizeSelect = el('select', { id: 'gen-size' });
    for (const [key, preset] of Object.entries(SIZE_PRESETS)) {
      const opt = el('option', { value: key }, preset.label);
      if (key === 'small') opt.selected = true;
      sizeSelect.appendChild(opt);
    }
    sizeGroup.appendChild(sizeSelect);
    dialog.appendChild(sizeGroup);

    // Custom size
    const customGroup = el('div', { className: 'form-row' });
    customGroup.appendChild(this._createInput('Width', 'gen-width', '40', 'number'));
    customGroup.appendChild(this._createInput('Height', 'gen-height', '30', 'number'));
    dialog.appendChild(customGroup);

    sizeSelect.addEventListener('change', () => {
      const preset = SIZE_PRESETS[sizeSelect.value];
      if (preset) {
        dialog.querySelector('#gen-width').value = preset.width;
        dialog.querySelector('#gen-height').value = preset.height;
      }
    });

    // Seed
    const seedGroup = this._createFormGroup('Seed');
    const seedRow = el('div', { className: 'form-row' });
    const seedInput = el('input', { type: 'text', id: 'gen-seed', value: String(Math.floor(Math.random() * 99999)), className: 'form-input' });
    const randomBtn = el('button', { className: 'btn btn-sm', onClick: () => { seedInput.value = Math.floor(Math.random() * 99999); } }, 'Random');
    seedRow.append(seedInput, randomBtn);
    seedGroup.appendChild(seedRow);
    dialog.appendChild(seedGroup);

    // Quality selector
    const qualityGroup = this._createFormGroup('Quality');
    const qualitySelect = el('select', { id: 'gen-quality' });
    qualitySelect.appendChild(el('option', { value: 'draft' }, 'Draft — fast, direct tile output'));
    const stdOpt = el('option', { value: 'standard' }, 'Standard — pipeline with transitions + cleanup');
    stdOpt.selected = true;
    qualitySelect.appendChild(stdOpt);
    qualitySelect.appendChild(el('option', { value: 'high' }, 'High — pipeline + WFC resolver + full decoration'));
    qualityGroup.appendChild(qualitySelect);
    dialog.appendChild(qualityGroup);

    // Options area (dynamic based on type)
    const optionsArea = el('div', { id: 'gen-options', className: 'gen-options' });
    dialog.appendChild(optionsArea);

    typeSelect.addEventListener('change', () => this._updateOptions(optionsArea, typeSelect.value));
    this._updateOptions(optionsArea, typeSelect.value);

    // Buttons
    const btnRow = el('div', { className: 'dialog-buttons' });
    const generateBtn = el('button', { className: 'btn btn-primary', onClick: () => this._generate(dialog) }, 'Generate');
    const cancelBtn = el('button', { className: 'btn', onClick: () => { overlay.remove(); this._dialog = null; } }, 'Cancel');
    btnRow.append(generateBtn, cancelBtn);
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    this._dialog = overlay;
  }

  _createFormGroup(label) {
    const group = el('div', { className: 'form-group' });
    group.appendChild(el('label', { className: 'form-label' }, label));
    return group;
  }

  _createInput(label, id, defaultValue, type = 'text') {
    const wrap = el('div', { className: 'form-field' });
    wrap.appendChild(el('label', { className: 'form-label-sm' }, label));
    wrap.appendChild(el('input', { type, id, value: defaultValue, className: 'form-input form-input-sm' }));
    return wrap;
  }

  _updateOptions(container, type) {
    container.innerHTML = '';
    const entry = GENERATOR_TYPES[type];
    if (!entry || !entry.defaultOptions) return;

    const opts = entry.defaultOptions;
    for (const [key, value] of Object.entries(opts)) {
      if (typeof value === 'boolean') {
        const label = el('label', { className: 'form-checkbox' });
        const input = el('input', { type: 'checkbox', 'data-option': key });
        input.checked = value;
        label.append(input, ` ${this._formatLabel(key)}`);
        container.appendChild(label);
      } else if (typeof value === 'number') {
        const group = el('div', { className: 'form-field-inline' });
        group.appendChild(el('label', { className: 'form-label-sm' }, this._formatLabel(key)));
        group.appendChild(el('input', {
          type: 'number', 'data-option': key, value: String(value),
          className: 'form-input form-input-sm',
          step: value < 1 ? '0.01' : '1',
        }));
        container.appendChild(group);
      }
    }
  }

  _formatLabel(key) {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
  }

  _generate(dialog) {
    const type = dialog.querySelector('#gen-type').value;
    const width = parseInt(dialog.querySelector('#gen-width').value, 10) || 40;
    const height = parseInt(dialog.querySelector('#gen-height').value, 10) || 30;
    const seed = dialog.querySelector('#gen-seed').value || Date.now();

    // Collect options
    const quality = dialog.querySelector('#gen-quality').value;
    const options = { width, height, seed, quality };
    const optionInputs = dialog.querySelectorAll('[data-option]');
    for (const input of optionInputs) {
      const key = input.dataset.option;
      if (input.type === 'checkbox') {
        options[key] = input.checked;
      } else if (input.type === 'number') {
        options[key] = parseFloat(input.value);
      } else {
        options[key] = input.value;
      }
    }

    const content = this.contentProvider.getContent(type);

    // Show progress indicator
    const generateBtn = dialog.querySelector('.btn-primary');
    const originalText = generateBtn.textContent;
    generateBtn.textContent = 'Generating...';
    generateBtn.disabled = true;

    if (this._worker) {
      // Off-thread generation via Web Worker
      const id = Date.now();
      const handler = (e) => {
        if (e.data.id !== id) return;
        this._worker.removeEventListener('message', handler);
        generateBtn.textContent = originalText;
        generateBtn.disabled = false;

        if (e.data.error) {
          console.error('Generation failed (worker):', e.data.error);
          alert(`Generation failed: ${e.data.error}`);
          return;
        }
        this._applyResult(e.data.tmj, type, seed);
      };
      this._worker.addEventListener('message', handler);
      this._worker.postMessage({ id, type, options, content });
    } else {
      // Main-thread fallback (for environments without Worker support)
      // Use setTimeout to let the UI update before blocking
      setTimeout(() => {
        try {
          const tmj = runGenerator(type, options, content);
          this._applyResult(tmj, type, seed);
        } catch (err) {
          console.error('Generation failed:', err);
          alert(`Generation failed: ${err.message}`);
        } finally {
          generateBtn.textContent = originalText;
          generateBtn.disabled = false;
        }
      }, 50);
    }
  }

  /** Apply a generated TMJ result to the editor. */
  _applyResult(tmj, type, seed) {
    const parsed = parseTMJ(tmj);

    // Preserve existing tilesetImages from the editor state.
    const existingImages = this.state.get('tilesetImages');
    const mergedImages = new Map(existingImages || []);
    if (parsed.tilesetImages instanceof Map) {
      for (const [k, v] of parsed.tilesetImages) mergedImages.set(k, v);
    }

    // Use the editor's existing tilesets if the generator didn't provide real image paths
    const existingTilesets = this.state.get('tilesets');
    const useTilesets = (existingTilesets && existingTilesets.length > 0)
      ? existingTilesets
      : parsed.tilesets;

    this.state.batch({
      ...parsed,
      tilesets: useTilesets,
      tilesetImages: mergedImages,
      dirty: true,
      scrollX: 0,
      scrollY: 0,
      zoom: 1,
      activeLayerIndex: 0,
      fileName: `${type}_${seed}.tmj`,
    });

    this.mapCanvas.invalidateAll();
    this.compositor.requestRender();

    // Close dialog
    if (this._dialog) {
      this._dialog.remove();
      this._dialog = null;
    }
  }
}
